/**
 * Helper functions for user session.
 * @author Prasenjit Sharan <prasenjit@helpshift.com>
 * @created 9 Jan, 2019
 * @module extras/sessionHelpers
 */

import lsUtil from "gunpowder/utils/localStorage";
import botsSessionConstants from "constants/botsSessionConstants";
import routingHelpers from "helpers/routing";
import {PREFERRED_LANG_LS_KEY} from "constants/pages";

const {SESSION_STORAGE_CONSTANTS} = botsSessionConstants;
const {
  SCROLL_POSITION,
  BOTS_LIST_PAGE_IS_ACTIVE,
  PUBLISHED_BOT_ID,
  BOTS_SEARCH_QUERY_IS_ACTIVE
} = SESSION_STORAGE_CONSTANTS;
const SESSION_TIMEOUT_STATUS = 401;
const SESSION_TIMEOUT_MESSAGE = "session timeout";
const LOGGED_OUT_LS_KEY = "ulo";
const NON_PII_LS_KEYS = [
  "issueDetails_issues_pane_width",
  "issueWrapper_issues_pane_width",
  "last_read_ann_ts",
  "analytics_sidebar_onboarding_done",
  PREFERRED_LANG_LS_KEY,
  LOGGED_OUT_LS_KEY
];

const INVALID_VALUES = [null, undefined, NaN];
const STRINGIFIED_INVALID_VALUES = INVALID_VALUES.map((v) => String(v));

/**
 * Predicate to check if the user session has timed out. This function checks
 * the status and error message sent with an XHR response. Session timeout logic
 * resides on the backend and it's communicated to frontend via XHRs.
 * @params {Object} xhr - The xhr object
 * @returns {boolean}
 */
const hasUserSessionTimedOut = (xhr) => {
  let response;

  try {
    response = xhr.responseText && JSON.parse(xhr.responseText);
  } catch (error) {
    return false;
  }

  // The status and data are sent by the moby backend. Please note that in order
  // to use this timeout predicate in other projects, you'll need your backend
  // to send the same status and data.
  return (
    xhr.status === SESSION_TIMEOUT_STATUS &&
    typeof response === "object" &&
    response.data === SESSION_TIMEOUT_MESSAGE
  );
};

/**
 * Returns the redirection path based on the current browser
 * address location
 *
 * @returns {string}
 */
const getRedirectionPath = () => {
  const basePath = routingHelpers.getAdminBasePath();

  const urlPaths = window.location.href.split(basePath);

  // The URL is of the format `https://phoenix.helpshift.com/admin/issues/0-5/`.
  // urlPaths [1] would be `issues/0-5/` in this example.
  // Or it can be of the format `https://phoenix.helpshift.com/e/admin/issue/5/
  // and in that case urlPaths [1] would be /issue/5/

  return urlPaths[1] ? `?next=${encodeURIComponent(`${basePath}${urlPaths[1]}`)}` : "";
};

/**
 * Handle user session timeouts. This fn navigates the app to the login page.
 * In order to take the user back to the page on which the timeout happened, it
 * adds the `next` query param to the login route.
 * NOTE - Clearly, this handler is moby specific and should not be used with other
 * projects such as mirkwood and comptroller.
 */
const onUserSessionTimeout = () => {
  const redirectToPath = getRedirectionPath();

  window.location.href = `/login/${redirectToPath}`;
};

/**
 * Returns all the reply language keys from localstorage for backup
 * @returns {Array.<string>} list of all the reply langugage keys
 */
const getReplyTranslationLanguageKeys = () => {
  // every reply translation key in LS ends with "-replyLang"
  const replyTranslationLangSearchRegex = /-replyLang$/g;

  return Object.keys(localStorage).filter((key) => key.match(replyTranslationLangSearchRegex));
};

/**
 * Clears PII from localStorage and retains non-PII information
 */
const clearPIIFromLs = () => {
  const backup = [];

  const replyTranslationLanguageKeys = getReplyTranslationLanguageKeys();
  const backupKeysList = [...replyTranslationLanguageKeys, ...NON_PII_LS_KEYS];

  // Take backup of keys we want to retain
  backupKeysList.forEach((key) => {
    const value = lsUtil.getItem(key);

    if (!STRINGIFIED_INVALID_VALUES.includes(String(value))) {
      backup.push([key, value]);
    }
  });

  // Clear everything
  localStorage.clear();

  // Restore backup
  backup.forEach((item) => {
    lsUtil.setItem(item[0], item[1]);
  });
};

/**
 * This will set the flag in local storage which indicates whether the user is
 * logged out to given value. We are storing the logged out flag so in any case
 * the local storage is not able to save this value, the user won't be
 * logged out. The downside is that the user might still appear online, but
 * avoiding incorrect logging is more important.
 * @param {boolean} userIsLoggedOut - Whether user is logged out or not
 */
const setUserLoggedOutStatusInLs = (userIsLoggedOut) => {
  lsUtil.setItem(LOGGED_OUT_LS_KEY, userIsLoggedOut);
};

/**
 * Returns the value of the flag indicating whether the user is logged in or not
 * from local storage. It will read the value as string from local storage and
 * return as a boolean.
 * @returns {boolean} - Value of the flag from local storage
 */
const getUserLoggedOutStatusFromLs = () => lsUtil.getItem(LOGGED_OUT_LS_KEY, true);

/**
 * Clears data related to the scroll position on the
 * bots list page.
 */
const clearBotsListScrollPos = () => {
  sessionStorage.removeItem(SCROLL_POSITION);
  sessionStorage.removeItem(BOTS_LIST_PAGE_IS_ACTIVE);
  sessionStorage.removeItem(PUBLISHED_BOT_ID);
  sessionStorage.removeItem(BOTS_SEARCH_QUERY_IS_ACTIVE);
};

export default {
  clearBotsListScrollPos,
  onUserSessionTimeout,
  hasUserSessionTimedOut,
  setUserLoggedOutStatusInLs,
  getUserLoggedOutStatusFromLs,
  clearPIIFromLs,
  getRedirectionPath
};
